import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AdminToStoreMessage,
  CMS_PROTOCOL_VERSION,
  CmsEditableKey,
  CmsFieldType,
  DistributiveOmit,
  isCmsMessage,
} from './protocol';

export interface CmsSelection {
  key: string;
  fieldType: CmsFieldType;
  value: string;
}

interface BridgeState {
  connected: boolean;
  route: string;
  keysOnScreen: CmsEditableKey[];
  selection: CmsSelection | null;
}

/**
 * Admin side of the CMS Edit Mode postMessage bridge.
 *
 * - Sends `cms:hello` when the iframe loads (call `onIframeLoad`).
 * - Tracks handshake state, current storefront route, editable keys on screen,
 *   and the last clicked key.
 * - Exposes senders for draft preview / navigation / highlight.
 *
 * Only accepts messages from the iframe's own window and from the storefront
 * origin derived from `storeUrl`.
 */
export const useCmsBridge = (storeUrl: string, brandId: number) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [state, setState] = useState<BridgeState>({ connected: false, route: '/', keysOnScreen: [], selection: null });

  const storeOrigin = (() => { try { return new URL(storeUrl).origin; } catch { return ''; } })();

  const send = useCallback((msg: DistributiveOmit<AdminToStoreMessage, 'v'>) => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !storeOrigin) return;
    win.postMessage({ v: CMS_PROTOCOL_VERSION, ...msg }, storeOrigin);
  }, [storeOrigin]);

  const hello = useCallback(() => send({ type: 'cms:hello', brandId, lang: 'en' }), [send, brandId]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isCmsMessage(event.data)) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (storeOrigin && event.origin !== storeOrigin) return;
      const msg = event.data;
      switch (msg.type) {
        case 'cms:ready':
          setState(s => ({ ...s, connected: true, route: msg.route, keysOnScreen: msg.keys }));
          break;
        case 'cms:route':
          setState(s => ({ ...s, route: msg.route }));
          break;
        case 'cms:keys':
          setState(s => ({ ...s, route: msg.route, keysOnScreen: msg.keys }));
          break;
        case 'cms:selected':
          setState(s => ({ ...s, selection: { key: msg.key, fieldType: msg.fieldType, value: msg.value } }));
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [storeOrigin]);

  // Reset when the target store changes.
  useEffect(() => {
    setState({ connected: false, route: '/', keysOnScreen: [], selection: null });
  }, [storeUrl, brandId]);

  return {
    iframeRef,
    ...state,
    onIframeLoad: hello,
    apply: (key: string, value: string) => send({ type: 'cms:apply', key, value }),
    clearDraft: (keys?: string[]) => send({ type: 'cms:clearDraft', keys }),
    navigate: (path: string) => send({ type: 'cms:navigate', path }),
    highlightAll: (on: boolean) => send({ type: 'cms:highlightAll', on }),
    select: (sel: CmsSelection | null) => setState(s => ({ ...s, selection: sel })),
  };
};
